export const REPORTING_LE_LENSES_PATH = '/api/reporting/le-lenses';

/** One natural-language filter request in, one allow-listed proposal back. The
 *  service uses a bounded Lens model call, then validates every returned key
 *  and value against the controls supplied by the board. */
export const REPORTING_LE_LENS_FILTER_PATH = `${REPORTING_LE_LENSES_PATH}/filter`;

/** A section's cards and the rows they are drawing in, one written reading back.
 *  One bounded, tool-free model call and no BigQuery job: the rows come from the
 *  client, so the reading describes the board the reader is looking at, filters
 *  and all, rather than the board as saved. Fail-soft server-side: an empty
 *  reading is a 200, because the section renders its cards without it. */
export const REPORTING_LE_LENS_READ_PATH = `${REPORTING_LE_LENSES_PATH}/read`;

/** SQL in, whether BigQuery would run it back. A dry run scans no bytes and
 *  creates no job, so a whole wave of generated queries can be checked on the
 *  build path for the price of one round trip. */
export const REPORTING_LE_LENS_DRY_RUN_PATH = `${REPORTING_LE_LENSES_PATH}/dry-run`;

export const REPORTING_LE_LENS_PROBE_PATH = `${REPORTING_LE_LENSES_PATH}/probe`;

export function reportingLeLensPath(lensId: string): string {
  return `${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}`;
}

export function reportingLeLensDataPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/data`;
}

export function reportingLeLensPdfPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/pdf`;
}

export function reportingLeLensCardDataPath(lensId: string, cardId: string): string {
  return `${reportingLeLensPath(lensId)}/cards/${encodeURIComponent(cardId)}/data`;
}

/** Add one answer-derived card without replacing the whole dashboard specification. */
export function reportingLeLensCardsPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/cards`;
}

export function reportingLeLensCertificationPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/certification`;
}

export const REPORTING_LE_LENS_CERTIFICATION_REQUESTS_PATH = `${REPORTING_LE_LENSES_PATH}/certification/requests`;

/** Types a candidate dataset query and reports what it would scan, without running it or
 *  billing for it. Stateless: not scoped to any saved Lens, so a dataset (or a chat pin) can be
 *  typed before anything exists to attach it to. */
export const REPORTING_LE_LENS_DATASET_DRY_RUN_PATH = `${REPORTING_LE_LENSES_PATH}/datasets/dry-run`;

/** Runs a candidate rewrite of an existing dataset once and reshapes every dependent card
 *  against the old and new rows, so a reader sees what would change before Apply. */
export function reportingLeLensDatasetExtendCheckPath(lensId: string, datasetId: string): string {
  return `${reportingLeLensPath(lensId)}/datasets/${encodeURIComponent(datasetId)}/extend-check`;
}

/** Previews folding one dataset into another (F7): every card reading `from` is reshaped against
 *  `into`'s current rows, the same response shape and owner-only gate as extend-check. */
export function reportingLeLensDatasetMergeCheckPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/datasets/merge-check`;
}

/** The governed dataset catalogue: what a person can anchor a Lens on without writing SQL. */
export const REPORTING_LE_LENS_CATALOGUE_PATH = `${REPORTING_LE_LENSES_PATH}/catalogue`;

export function reportingLeLensCataloguePath(catalogueId: string): string {
  return `${REPORTING_LE_LENS_CATALOGUE_PATH}/${encodeURIComponent(catalogueId)}`;
}

/** Ranked values for a planner-chosen governed field before the Lens is created. */
export function reportingLeLensCatalogueValuesPath(catalogueId: string): string {
  return `${reportingLeLensCataloguePath(catalogueId)}/values`;
}

/** One request against a governed dataset in, one card configuration out. One bounded model call,
 *  no BigQuery job: the server only ever names fields the catalogue declares. */
export const REPORTING_LE_LENS_COMPOSE_PATH = `${REPORTING_LE_LENSES_PATH}/compose`;

/** Question-first search across governed datasets, their fields and example questions. */
export const REPORTING_LE_LENS_CATALOGUE_SEARCH_PATH = `${REPORTING_LE_LENS_CATALOGUE_PATH}/search`;

export const REPORTING_ANALYTICS_DOMAINS_PATH = `${REPORTING_LE_LENSES_PATH}/analytics/domains`;

export function reportingAnalyticsDomainPath(domainId: string): string {
  return `${REPORTING_ANALYTICS_DOMAINS_PATH}/${encodeURIComponent(domainId)}`;
}

export function reportingAnalyticsDomainSnapshotPath(domainId: string): string {
  return `${reportingAnalyticsDomainPath(domainId)}/snapshot`;
}

export function reportingAnalyticsDomainBreakdownPath(domainId: string, breakdownId: string): string {
  return `${reportingAnalyticsDomainPath(domainId)}/breakdowns/${encodeURIComponent(breakdownId)}`;
}

/** The distinct values of one declared dimension, for the chart-filter picker. */
export function reportingLeLensDatasetValuesPath(lensId: string, datasetId: string): string {
  return `${reportingLeLensPath(lensId)}/datasets/${encodeURIComponent(datasetId)}/values`;
}

/** Team spaces: a Lens's durable home, and the membership that decides who can open its work. */
export const REPORTING_LE_LENS_SPACES_PATH = `${REPORTING_LE_LENSES_PATH}/spaces`;

export function reportingLeLensSpacePath(spaceId: string): string {
  return `${REPORTING_LE_LENS_SPACES_PATH}/${encodeURIComponent(spaceId)}`;
}

export function reportingLeLensSpaceMembersPath(spaceId: string): string {
  return `${reportingLeLensSpacePath(spaceId)}/members`;
}

export function reportingLeLensAccessRequestPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/access-request`;
}

/** Who can see one Lens and where it lives. */
export function reportingLeLensSharingPath(lensId: string): string {
  return `${reportingLeLensPath(lensId)}/sharing`;
}
